import { SuggestionItem } from "@/types/suggestions";

export const defaultReminderSmsTemplate =
  "Hi {first_name}, this is a reminder for your appointment with {provider_name} at {start}. {location} {forms} Please confirm your appointment, without confirmation your spot will not be guaranteed. Text 1- To Confirm or 2- To Cancel.";

export const defaultConfirmationSmsTemplate =
  "Hi {first_name}, thank you for booking with {tenant_name}! Your appointment with {provider_name} is confirmed for {start}. {location} {forms}";

export const defaultCancellationSmsTemplate =
  "Hi {first_name}, your appointment with {provider_name} scheduled for {start} has been cancelled.";

export const defaultNoShowSmsTemplate =
  "Hi {first_name}, we noticed you missed your appointment with {provider_name} scheduled for {start}. Please contact us to reschedule.";

export const defaultFollowUpSmsTemplate =
  "Hi {first_name}, thank you for coming to your appointment with {provider_name} at {start}! We hope you enjoyed your time with us.";

export const overrideNoticeSmsTemplate =
  "Hey {first_name}, this is a reminder that your appointment with {tenant_name} is coming up in {minute_text}. We haven't received your confirmation yet. Your spot is now open for others to book. Please respond to this message to either confirm or reschedule.";

/** Mirrors `format_location_for_message` on the backend. */
export function formatLocationSentence(location?: {
  isVirtual?: boolean;
  addressLineOne?: string;
  addressLineTwo?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}): string {
  if (!location) return "";
  if (location.isVirtual) return "The appointment will be online.";
  const address = formatLocationBare(location);
  return address ? `The appointment is located at ${address}.` : "";
}

/** Mirrors `substitute_location` on the backend. */
export function substituteLocation(
  template: string,
  location?: Parameters<typeof formatLocationBare>[0],
): string {
  const sentence = formatLocationSentence(location);
  const bare = formatLocationBare(location);
  const EMPTY = "\u0000";

  const result = template.replace(
    /\{location(?:\|[^}]*)?\}(\s*\.)?/g,
    (_match, trailing: string | undefined, offset: number) => {
      const preceding = template.slice(0, offset).trimEnd();
      const isInline = /[a-z0-9]$/i.test(preceding);
      const value = isInline ? bare : sentence;
      if (!value) return EMPTY + (isInline ? trailing || "" : "");
      if (trailing && value.endsWith(".")) return value;
      return value + (trailing || "");
    },
  );

  return result
    .replace(new RegExp(`\\s*\\b(?:at|in|@)\\b\\s*${EMPTY}`, "g"), "")
    .split(EMPTY)
    .join("")
    .replace(/[ \t]{2,}/g, " ");
}

/** Fill `{location}` for a staff-composed text, or leave it for the server.
 *
 * A visit whose main services are all virtual is by video though its location is
 * a clinic. The server words that as online and adds the Meet link, which the
 * browser doesn't have yet, so the token stays for it to fill. */
export function prefillLocation(
  template: string,
  location: Parameters<typeof substituteLocation>[1],
  mainServices: { isVirtual?: boolean }[],
): string {
  const byVideoFromClinic =
    !location?.isVirtual && isVideoVisit(location, mainServices);
  return byVideoFromClinic ? template : substituteLocation(template, location);
}

/** Mirrors `is_virtual_appointment` on the backend: a visit is by video at a
 * virtual location, or when its main services are all virtual. */
export function isVideoVisit(
  location: { isVirtual?: boolean } | undefined,
  mainServices: { isVirtual?: boolean }[],
): boolean {
  return (
    Boolean(location?.isVirtual) ||
    (mainServices.length > 0 &&
      mainServices.every((service) => service.isVirtual))
  );
}

/** The zone a video visit's `{start}` ends with (" EDT"), as
 * `format_appointment_start` writes it on the backend: the patient may be
 * anywhere, and the time is the clinic's. Empty for an in-person visit. */
export function startZoneSuffix(
  start: Date,
  timeZone: string,
  byVideo: boolean,
): string {
  if (!byVideo) return "";
  const zone = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "short",
  })
    .formatToParts(start)
    .find((part) => part.type === "timeZoneName")?.value;
  return zone ? ` ${zone}` : "";
}

/** Mirrors `format_location_bare` on the backend. */
export function formatLocationBare(location?: {
  isVirtual?: boolean;
  addressLineOne?: string;
  addressLineTwo?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}): string {
  if (!location) return "";
  if (location.isVirtual) return "online";

  const collapse = (value?: string) =>
    (value || "").replace(/\s+/g, " ").trim();
  const street = [
    collapse(location.addressLineOne),
    collapse(location.addressLineTwo),
  ]
    .filter(Boolean)
    .join(", ");
  const cityStateZip = [
    collapse(location.city),
    [collapse(location.state).toUpperCase(), collapse(location.zipCode)]
      .filter(Boolean)
      .join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  return [street, cityStateZip].filter(Boolean).join(", ");
}

export const defaultBookingFeeText =
  "This appointment has a booking fee to secure your spot.";

/**
 * SMS merge fields not returned by `GET /suggestions` (they are supplied at send
 * time via `additional_suggestions`). Appended in `SMSTemplateEditor` after
 * `useSuggestions(["PATIENT","TENANT","EVENT"])`.
 */
export const smsExtraTemplateSuggestions: SuggestionItem[] = [
  {
    id: "minute_text",
    label: "Minute Text",
    content: "minute_text",
    type: "TENANT",
  },
];

/**
 * Formats an SMS template value for display in summary views.
 * Truncates long values to 40 characters with ellipsis.
 *
 * @param value - The SMS template string value to format
 * @returns Formatted string: "Not set" if empty, full value if <= 40 chars, or truncated with "..."
 */
export function formatSmsTemplateValue(
  value: string | undefined | null,
): string {
  if (!value) return "Not set";
  if (value.length > 40) return `${value.slice(0, 40)}...`;
  return value;
}

/**
 * Fill an SMS template's `{tokens}` from a values map — mirrors how the backend
 * composes the confirmation from the template. Tokens without a value (e.g.
 * `{forms}`, which needs the created event) are dropped and whitespace tidied.
 *
 * Pass `keepUnresolved` when the filled text may be sent back for the backend
 * to hydrate: unresolved tokens are then left as `{token}` rather than dropped,
 * so editing the text cannot silently delete dynamic content the console can't
 * resolve. `{forms}` needs the created event (and is cleared once the patient
 * has completed the required forms) and `{meeting_link}` is minted at send
 * time; the backend's `SafeFormatter` returns an unknown `{token}` untouched and
 * `send_message` runs `hydrate_text` over every outbound body, override
 * included. Blanking them here would be permanent: a staff edit is sent as
 * `sms_override`, and by then the token is already gone.
 */
export function fillSmsTemplate(
  template: string,
  values: Record<string, string | undefined | null>,
  options: { keepUnresolved?: boolean } = {},
): string {
  return template
    .replace(/\{([^}]+)\}/g, (full: string, inner: string) => {
      const id = String(inner).split("|")[0].trim();
      const value = values[id];
      if (value != null && value !== "") return value;
      return options.keepUnresolved ? full : "";
    })
    .replace(/[ \t]{2,}/g, " ")
    .replace(/ +\n/g, "\n")
    .trim();
}

/** Backend uses this when no patient/event context exists for a suggestion. */
export const SUGGESTION_PREVIEW_PLACEHOLDER = "Preview Not Available";

/**
 * True when `GET /suggestions` returned a value we should substitute in UI (not empty,
 * not the generic preview placeholder).
 */
export function isHydratableSuggestionContent(
  content: string | undefined,
): boolean {
  if (content === undefined || content.length === 0) return false;
  if (content === SUGGESTION_PREVIEW_PLACEHOLDER) return false;
  return true;
}

/**
 * Replace `{token}` (and `{token|label}`) only when `suggestions` has a hydratable
 * `content` for that id. Other tokens stay as `{...}`.
 */
export function hydrateSmsTemplateFromSuggestions(
  text: string,
  suggestions: SuggestionItem[],
): string {
  const byId = new Map(suggestions.map((s) => [s.id, s.content]));
  return text.replace(/\{([^}]+)\}/g, (full, inner: string) => {
    const raw = String(inner);
    const id = raw.includes("|")
      ? raw.slice(0, raw.indexOf("|")).trim()
      : raw.trim();
    const content = byId.get(id);
    if (isHydratableSuggestionContent(content)) {
      return content!;
    }
    return full;
  });
}
